import { create } from "zustand";
import { canDrawDriveView } from "./webgl";

// Qué unidad se está viendo en la vista de manejo. Vive fuera de la tarjeta:
// la vista va sobre el mapa y debe seguir abierta aunque la tarjeta se cierre
// o la lista pase a compacta.
//
// Se abre sola cada vez que se selecciona una unidad, en la lista o en el mapa
// (Daniel, 29-oct). Al cerrarla con ✕ la primera vez se pregunta «¿te gustó?»
// (beta): con «no» o cerrando la pregunta sin contestar deja de abrirse sola en
// ese navegador; el botón del encabezado la sigue abriendo a mano.
const OLD_AUTO_KEY = "driveView.autoOpen";
// El botón palpita hasta que se ve la vista por primera vez (pedido de Daniel
// para que la prueben); después se queda quieto: palpitando siempre cansa.
const TRIED_KEY = "driveView.tried";
// Ya se le preguntó (contestara o no): la pregunta sale una sola vez.
const ASKED_KEY = "driveView.feedbackAsked";
// No le gustó o le dio igual: ya no se abre sola al seleccionar.
const NO_AUTO_KEY = "driveView.noAutoOpen";

export type DriveViewVote = "like" | "dislike" | "meh";

const read = (key: string) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};
const write = (key: string) => {
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* sin almacenamiento: vale sólo para esta sesión */
  }
};
try {
  localStorage.removeItem(OLD_AUTO_KEY);
} catch {
  /* sin almacenamiento: nada que borrar */
}

interface DriveViewState {
  vehicleId: string | null;
  expanded: boolean;
  /** Ya la vio alguna vez en este navegador (el botón deja de palpitar). */
  tried: boolean;
  /** Ya se le preguntó si le gustó. */
  asked: boolean;
  /** No le gustó o le dio igual: no se abre sola al seleccionar. */
  noAutoOpen: boolean;
  /** La pregunta «¿te gustó?» está en pantalla. */
  asking: boolean;
  /** Quién la abrió (la selección o el botón). */
  openedBy: "auto" | "user" | null;
  /** Botón del encabezado (o del mapa en móvil): abre o cierra. */
  toggle: (vehicleId: string) => void;
  /** La unidad seleccionada cambió (null = ninguna). */
  follow: (vehicleId: string | null) => void;
  /** ✕ de la ventanita: cierra; la primera vez, pregunta si le gustó. */
  dismiss: () => void;
  /** Respuesta a la pregunta («meh» = la cerró sin contestar). Devuelve el voto a enviar. */
  answer: (vote: DriveViewVote) => DriveViewVote;
  /** Cierra sin preguntar (cambio de compañía, la unidad ya no está). */
  close: () => void;
  setExpanded: (expanded: boolean) => void;
}

export const useDriveView = create<DriveViewState>((set, get) => ({
  vehicleId: null,
  expanded: false,
  tried: read(TRIED_KEY),
  asked: read(ASKED_KEY),
  noAutoOpen: read(NO_AUTO_KEY),
  asking: false,
  openedBy: null,
  toggle: (vehicleId) => {
    if (!canDrawDriveView()) return;
    if (get().vehicleId === vehicleId) {
      set({ vehicleId: null, expanded: false, openedBy: null });
      return;
    }
    write(TRIED_KEY);
    set({ vehicleId, openedBy: "user", tried: true });
  },
  follow: (vehicleId) => {
    const s = get();
    if (vehicleId) {
      if (!canDrawDriveView()) return;
      // «No se abre sola» aplica sólo con la vista cerrada. Abierta (p. ej. con el
      // botón), sigue a la unidad seleccionada: antes se quedaba en la primera y
      // mostraba una unidad que ya no era la del mapa (Daniel, 29-oct).
      if (s.vehicleId === vehicleId || (s.noAutoOpen && !s.vehicleId)) return;
      write(TRIED_KEY);
      set({ vehicleId, openedBy: s.vehicleId ? s.openedBy : "auto", tried: true });
    } else if (s.vehicleId) {
      // Deseleccionar (o clic en el mapa) la cierra aunque se haya abierto con el
      // botón: sin unidad seguida, la vista mostraría algo que ya no se ve (Daniel, 29-oct).
      set({ vehicleId: null, expanded: false, openedBy: null });
    }
  },
  dismiss: () => {
    const ask = !get().asked;
    set({ vehicleId: null, expanded: false, openedBy: null, asking: ask });
  },
  answer: (vote) => {
    write(ASKED_KEY);
    if (vote !== "like") write(NO_AUTO_KEY);
    set({ asking: false, asked: true, noAutoOpen: vote !== "like" });
    return vote;
  },
  close: () => {
    if (get().vehicleId) set({ vehicleId: null, expanded: false, openedBy: null });
  },
  setExpanded: (expanded) => set({ expanded }),
}));
